// 서버 API 호출 래퍼. 인증 실패(401)는 onUnauthorized 콜백으로 위임.
import type {
  AuthState,
  Category,
  Post,
  Attachment,
  LoginInput,
  PasswordChangeInput,
  InitInput,
  CategoryCreateInput,
  CategoryUpdateInput,
  PostCreateInput,
  PostUpdateInput,
} from '@shared/types';
import type {
  Company,
  Site,
  Issue,
  CompanyInput,
  SiteInput,
  IssueInput,
} from '@shared/manage';
import type {
  Platform,
  SaasProduct,
  ProductInput,
  ProductPatch,
  RequestSummary,
  RequestDetail,
} from '@shared/requests';

export class ApiError extends Error {
  readonly status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

export class ApiClient {
  private onUnauthorized: (() => void) | null = null;

  setUnauthorizedHandler(fn: () => void): void {
    this.onUnauthorized = fn;
  }

  private async request<T>(path: string, init?: RequestInit): Promise<T> {
    const res = await fetch(path, {
      ...init,
      headers: { 'content-type': 'application/json', ...(init?.headers ?? {}) },
    });
    if (res.status === 401 && this.onUnauthorized) this.onUnauthorized();
    if (!res.ok) {
      const data = (await res.json().catch(() => ({}))) as { error?: string };
      throw new ApiError(res.status, data.error ?? `요청 실패 (${res.status})`);
    }
    if (res.status === 204) return undefined as T;
    return (await res.json()) as T;
  }

  // ---- 인증 ----
  me(): Promise<AuthState> {
    return this.request('/api/auth/me');
  }
  initSetup(input: InitInput): Promise<{ ok: true }> {
    return this.request('/api/setup/init', { method: 'POST', body: JSON.stringify(input) });
  }
  login(input: LoginInput): Promise<{ ok: true }> {
    return this.request('/api/auth/login', { method: 'POST', body: JSON.stringify(input) });
  }
  logout(): Promise<{ ok: true }> {
    return this.request('/api/auth/logout', { method: 'POST' });
  }
  changePassword(input: PasswordChangeInput): Promise<{ ok: true }> {
    return this.request('/api/auth/password', { method: 'POST', body: JSON.stringify(input) });
  }

  // ---- 카테고리 ----
  listCategories(): Promise<Category[]> {
    return this.request('/api/categories');
  }
  createCategory(input: CategoryCreateInput): Promise<Category> {
    return this.request('/api/categories', { method: 'POST', body: JSON.stringify(input) });
  }
  updateCategory(id: number, input: CategoryUpdateInput): Promise<Category> {
    return this.request(`/api/categories/${id}`, { method: 'PUT', body: JSON.stringify(input) });
  }
  deleteCategory(id: number): Promise<void> {
    return this.request(`/api/categories/${id}`, { method: 'DELETE' });
  }
  reorderCategories(orderedIds: number[]): Promise<{ ok: true }> {
    return this.request('/api/categories/reorder', {
      method: 'POST',
      body: JSON.stringify({ orderedIds }),
    });
  }

  // ---- 글 ----
  listPosts(categoryId: number): Promise<Post[]> {
    return this.request(`/api/posts?categoryId=${categoryId}`);
  }
  getPost(id: number): Promise<Post> {
    return this.request(`/api/posts/${id}`);
  }
  createPost(input: PostCreateInput, notify = false): Promise<Post> {
    return this.request(`/api/posts${notify ? '?notify=1' : ''}`, {
      method: 'POST',
      body: JSON.stringify(input),
    });
  }
  updatePost(id: number, input: PostUpdateInput, notify = false): Promise<Post> {
    return this.request(`/api/posts/${id}${notify ? '?notify=1' : ''}`, {
      method: 'PUT',
      body: JSON.stringify(input),
    });
  }
  deletePost(id: number, notify = false): Promise<void> {
    return this.request(`/api/posts/${id}${notify ? '?notify=1' : ''}`, { method: 'DELETE' });
  }
  /** 글 읽음수 +1 → 갱신된 읽음수 */
  markPostRead(id: number): Promise<{ readCount: number }> {
    return this.request(`/api/posts/${id}/read`, { method: 'POST' });
  }

  // ---- 웹푸시 ----
  pushPublicKey(): Promise<{ publicKey: string }> {
    return this.request('/api/push/key');
  }
  pushSubscribe(subscription: unknown): Promise<{ ok: true }> {
    return this.request('/api/push/subscribe', {
      method: 'POST',
      body: JSON.stringify(subscription),
    });
  }
  pushUnsubscribe(endpoint: string): Promise<{ ok: true }> {
    return this.request('/api/push/unsubscribe', {
      method: 'POST',
      body: JSON.stringify({ endpoint }),
    });
  }

  // ---- 첨부 (multipart) ----
  async uploadAttachments(postId: number, files: File[]): Promise<Attachment[]> {
    const form = new FormData();
    form.set('postId', String(postId));
    for (const f of files) form.append('files', f);
    const res = await fetch('/api/attachments/upload', { method: 'POST', body: form });
    if (res.status === 401 && this.onUnauthorized) this.onUnauthorized();
    if (!res.ok) {
      const data = (await res.json().catch(() => ({}))) as { error?: string };
      throw new ApiError(res.status, data.error ?? '업로드 실패');
    }
    return (await res.json()) as Attachment[];
  }
  deleteAttachment(id: number): Promise<void> {
    return this.request(`/api/attachments/${id}`, { method: 'DELETE' });
  }

  // ---- 운영관리: 업체 ----
  listCompanies(): Promise<Company[]> {
    return this.request('/api/manage/companies');
  }
  createCompany(input: CompanyInput): Promise<Company> {
    return this.request('/api/manage/companies', { method: 'POST', body: JSON.stringify(input) });
  }
  updateCompany(id: number, input: Partial<CompanyInput>): Promise<Company> {
    return this.request(`/api/manage/companies/${id}`, { method: 'PUT', body: JSON.stringify(input) });
  }
  deleteCompany(id: number): Promise<void> {
    return this.request(`/api/manage/companies/${id}`, { method: 'DELETE' });
  }

  // ---- 운영관리: 사이트 ----
  listSites(companyId: number): Promise<Site[]> {
    return this.request(`/api/manage/sites?companyId=${companyId}`);
  }
  listServiceSites(): Promise<Site[]> {
    return this.request('/api/manage/sites?serviceOnly=1');
  }
  createSite(input: SiteInput): Promise<Site> {
    return this.request('/api/manage/sites', { method: 'POST', body: JSON.stringify(input) });
  }
  updateSite(id: number, input: Partial<SiteInput>): Promise<Site> {
    return this.request(`/api/manage/sites/${id}`, { method: 'PUT', body: JSON.stringify(input) });
  }
  deleteSite(id: number): Promise<void> {
    return this.request(`/api/manage/sites/${id}`, { method: 'DELETE' });
  }

  // ---- 운영관리: 이슈 ----
  listIssues(siteId: number): Promise<Issue[]> {
    return this.request(`/api/manage/issues?siteId=${siteId}`);
  }
  createIssue(input: IssueInput): Promise<Issue> {
    return this.request('/api/manage/issues', { method: 'POST', body: JSON.stringify(input) });
  }
  updateIssue(id: number, input: Partial<IssueInput>): Promise<Issue> {
    return this.request(`/api/manage/issues/${id}`, { method: 'PUT', body: JSON.stringify(input) });
  }
  deleteIssue(id: number): Promise<void> {
    return this.request(`/api/manage/issues/${id}`, { method: 'DELETE' });
  }

  // ---- 신청·문의: SaaS 상품 ----
  listProducts(platform?: Platform): Promise<SaasProduct[]> {
    return this.request(`/api/manage/products${platform ? `?platform=${platform}` : ''}`);
  }
  createProduct(input: ProductInput): Promise<SaasProduct> {
    return this.request('/api/manage/products', { method: 'POST', body: JSON.stringify(input) });
  }
  updateProduct(id: number, input: ProductPatch): Promise<SaasProduct> {
    return this.request(`/api/manage/products/${id}`, { method: 'PUT', body: JSON.stringify(input) });
  }
  deleteProduct(id: number): Promise<void> {
    return this.request(`/api/manage/products/${id}`, { method: 'DELETE' });
  }

  // ---- 신청·문의: 접수 ----
  listRequests(filter: { kind?: string; platform?: string; status?: string } = {}): Promise<RequestSummary[]> {
    const q = new URLSearchParams();
    if (filter.kind) q.set('kind', filter.kind);
    if (filter.platform) q.set('platform', filter.platform);
    if (filter.status) q.set('status', filter.status);
    const qs = q.toString();
    return this.request(`/api/manage/requests${qs ? `?${qs}` : ''}`);
  }
  getRequest(id: number): Promise<RequestDetail> {
    return this.request(`/api/manage/requests/${id}`);
  }
  updateRequestStatus(id: number, status: string): Promise<RequestSummary> {
    return this.request(`/api/manage/requests/${id}`, {
      method: 'PUT',
      body: JSON.stringify({ status }),
    });
  }
  deleteRequest(id: number): Promise<void> {
    return this.request(`/api/manage/requests/${id}`, { method: 'DELETE' });
  }
  async replyRequest(id: number, content: string, files: File[]): Promise<RequestDetail> {
    const form = new FormData();
    form.set('content', content);
    for (const f of files) form.append('files', f);
    const res = await fetch(`/api/manage/requests/${id}/messages`, { method: 'POST', body: form });
    if (res.status === 401 && this.onUnauthorized) this.onUnauthorized();
    if (!res.ok) {
      const data = (await res.json().catch(() => ({}))) as { error?: string };
      throw new ApiError(res.status, data.error ?? '답변 등록 실패');
    }
    return (await res.json()) as RequestDetail;
  }
  deleteRequestAttachment(id: number): Promise<void> {
    return this.request(`/api/manage/requests/attachments/${id}`, { method: 'DELETE' });
  }
  /** 접수 읽음수 +1 → 갱신된 읽음수 */
  markRequestRead(id: number): Promise<{ readCount: number }> {
    return this.request(`/api/manage/requests/${id}/read`, { method: 'POST' });
  }
}

// 앱 전역 단일 인스턴스
export const api = new ApiClient();
